import React, { useEffect, useState } from 'react';
import {
  Table,
  Tag,
  Button,
  message,
  Select,
  Card,
  Typography,
  Space,
  Grid,
  Empty,
  Spin,
  Statistic,
  Row,
  Col,
  Segmented,
  Input,
} from 'antd';
import {
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  CarOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  getAllOrders,
  deliverOrder,
  assignOrderToLivreur,
  Order,
} from '../../services/orderService';
import { zoneService, Zone } from '../../services/zoneService';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

type ViewMode = 'livraisons' | 'statistiques';

const normalizePhone = (value: string) => (value || '').replace(/\D/g, '');

const DeliveryDriverView: React.FC = () => {
  const [pendingOrders, setPendingOrders] = useState<Order[]>([]);
  const [allDeliveryOrders, setAllDeliveryOrders] = useState<Order[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZoneIds, setSelectedZoneIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>('livraisons');
  const [statsPeriod, setStatsPeriod] = useState<'today' | '7days' | '30days'>('today');
  const [searchTerm, setSearchTerm] = useState('');
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    const init = async () => {
      await fetchZones();
      await refreshData();
    };
    init();
  }, []);

  useEffect(() => {
    if (viewMode === 'statistiques') {
      fetchStatsOrders();
    }
  }, [statsPeriod, selectedZoneIds, viewMode]);

  const getStatsDateRange = () => {
    const end = dayjs().endOf('day');
    if (statsPeriod === '7days') return [dayjs().subtract(7, 'day').startOf('day'), end] as const;
    if (statsPeriod === '30days') return [dayjs().subtract(30, 'day').startOf('day'), end] as const;
    return [dayjs().startOf('day'), end] as const;
  };

  const fetchZones = async () => {
    try {
      const data = await zoneService.getAllZones();
      setZones(data);
    } catch {
      message.error('Erreur lors du chargement des zones');
    }
  };

  const refreshData = async (zoneIds: string[] = selectedZoneIds) => {
    await fetchPendingOrders(zoneIds);
    if (viewMode === 'statistiques') {
      await fetchStatsOrders(zoneIds);
    }
  };

  const fetchPendingOrders = async (zoneIds: string[] = selectedZoneIds) => {
    try {
      setLoading(true);
      const { orders: data } = await getAllOrders({
        startDate: dayjs().subtract(30, 'day').startOf('day').toISOString(),
        endDate: dayjs().endOf('day').toISOString(),
        limit: 10000,
        deliveryOnly: true,
        zoneIds: zoneIds.length > 0 ? zoneIds : undefined,
      });
      setPendingOrders(data.filter((o) => !o.isDelivered));
    } catch {
      message.error('Erreur lors du chargement des livraisons');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatsOrders = async (zoneIds: string[] = selectedZoneIds) => {
    try {
      setLoading(true);
      const [start, end] = getStatsDateRange();
      const { orders: data } = await getAllOrders({
        startDate: start.toISOString(),
        endDate: end.toISOString(),
        limit: 10000,
        deliveryOnly: true,
        zoneIds: zoneIds.length > 0 ? zoneIds : undefined,
      });
      setAllDeliveryOrders(data);
    } catch {
      message.error('Erreur lors du chargement des statistiques');
    } finally {
      setLoading(false);
    }
  };

  const handleZonesChange = (values: string[]) => {
    setSelectedZoneIds(values);
    fetchPendingOrders(values);
    if (viewMode === 'statistiques') {
      fetchStatsOrders(values);
    }
  };

  const handleTakeCharge = async (orderId: string) => {
    try {
      await assignOrderToLivreur(orderId);
      message.success('Livraison prise en charge');
      refreshData();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Erreur');
    }
  };

  const handleDeliver = async (orderId: string) => {
    try {
      await deliverOrder(orderId);
      message.success('Commande marquée comme livrée');
      refreshData();
    } catch {
      message.error('Erreur lors de la mise à jour');
    }
  };

  const getAddress = (record: Order) => {
    const addr = record.shippingAddress as any;
    if (!addr) return '—';
    return [addr.street || addr.address, addr.city].filter(Boolean).join(', ') || '—';
  };

  const getDeliveryFee = (order: Order) =>
    order.deliveryZone?.price ?? order.shippingPrice ?? 0;

  const getZoneName = (order: Order) => order.deliveryZone?.name || 'Sans zone';

  const matchesSearch = (order: Order) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    const fullName = `${order.personalInfo?.firstName || ''} ${order.personalInfo?.lastName || ''}`.toLowerCase();
    const phone = (order.personalInfo?.phone || '').toLowerCase();
    const phoneDigits = normalizePhone(order.personalInfo?.phone || '');
    const queryDigits = normalizePhone(q);
    return fullName.includes(q) || phone.includes(q) || (!!queryDigits && phoneDigits.includes(queryDigits));
  };

  const visiblePendingOrders = pendingOrders.filter(matchesSearch);

  // Stats
  const deliveredOrders = allDeliveryOrders.filter((o) => o.isDelivered);
  const pendingInPeriod = allDeliveryOrders.filter((o) => !o.isDelivered);
  const deliveryRevenue = deliveredOrders.reduce((sum, o) => sum + getDeliveryFee(o), 0);
  const pendingRevenue = pendingInPeriod.reduce((sum, o) => sum + getDeliveryFee(o), 0);

  const deliveryByZoneMap: Record<string, { count: number; amount: number; pending: number }> = {};
  allDeliveryOrders.forEach((order) => {
    const name = getZoneName(order);
    if (!deliveryByZoneMap[name]) {
      deliveryByZoneMap[name] = { count: 0, amount: 0, pending: 0 };
    }
    if (order.isDelivered) {
      deliveryByZoneMap[name].count += 1;
      deliveryByZoneMap[name].amount += getDeliveryFee(order);
    } else {
      deliveryByZoneMap[name].pending += 1;
    }
  });
  const deliveryByZone = Object.entries(deliveryByZoneMap)
    .map(([name, data]) => ({ name, ...data }))
    .sort((a, b) => b.amount - a.amount);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString('fr-FR'),
      width: 140,
    },
    {
      title: 'Client',
      key: 'client',
      render: (_: any, record: Order) =>
        `${record.personalInfo?.firstName || ''} ${record.personalInfo?.lastName || ''}`,
    },
    {
      title: 'Téléphone',
      key: 'phone',
      render: (_: any, record: Order) =>
        record.personalInfo?.phone ? (
          <a href={`tel:${record.personalInfo.phone}`}>{record.personalInfo.phone}</a>
        ) : (
          '—'
        ),
    },
    {
      title: 'Zone',
      key: 'zone',
      render: (_: any, record: Order) => (
        <Tag color="blue">{getZoneName(record)}</Tag>
      ),
    },
    {
      title: 'Montant livraison',
      key: 'shipping',
      render: (_: any, record: Order) => (
        <Text strong style={{ color: '#5e35b1' }}>
          {getDeliveryFee(record).toLocaleString('fr-FR')} FCFA
        </Text>
      ),
    },
    {
      title: 'Adresse',
      key: 'address',
      render: (_: any, record: Order) => getAddress(record),
    },
    {
      title: 'Statut',
      key: 'assigned',
      render: (_: any, record: Order) =>
        record.assignedLivreur?.name ? (
          <Tag color="green">{record.assignedLivreur.name}</Tag>
        ) : (
          <Tag color="orange">Libre</Tag>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Order) => (
        <Space wrap>
          {!record.assignedLivreur?.user && (
            <Button type="primary" size="small" onClick={() => handleTakeCharge(record._id)}>
              Prendre en charge
            </Button>
          )}
          <Button size="small" onClick={() => handleDeliver(record._id)}>
            Livrer
          </Button>
        </Space>
      ),
    },
  ];

  const renderMobileCard = (record: Order) => {
    const phone = record.personalInfo?.phone;
    const fee = getDeliveryFee(record);
    return (
      <Card
        key={record._id}
        size="small"
        style={{ marginBottom: 12, borderRadius: 12 }}
        styles={{ body: { padding: 14 } }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
          <div>
            <Tag color="blue" style={{ fontSize: 13, marginBottom: 6 }}>
              <EnvironmentOutlined /> {getZoneName(record)}
            </Tag>
            <div>
              <Text strong style={{ fontSize: 18, color: '#5e35b1' }}>
                {fee.toLocaleString('fr-FR')} FCFA
              </Text>
              <div>
                <Text type="secondary" style={{ fontSize: 12 }}>Montant livraison</Text>
              </div>
            </div>
          </div>
          {record.assignedLivreur?.name ? (
            <Tag color="green">Pris</Tag>
          ) : (
            <Tag color="orange">Libre</Tag>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          <div style={{ marginBottom: 6 }}>
            <UserOutlined style={{ color: '#888' }} />{' '}
            <Text>
              {`${record.personalInfo?.firstName || ''} ${record.personalInfo?.lastName || ''}`.trim() || 'Client'}
            </Text>
          </div>
          {phone && (
            <div style={{ marginBottom: 6 }}>
              <PhoneOutlined style={{ color: '#888' }} />{' '}
              <a href={`tel:${phone}`} style={{ fontSize: 15 }}>{phone}</a>
            </div>
          )}
          <div style={{ marginBottom: 4 }}>
            <EnvironmentOutlined style={{ color: '#888' }} /> <Text>{getAddress(record)}</Text>
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {new Date(record.createdAt).toLocaleString('fr-FR')}
          </Text>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          {!record.assignedLivreur?.user && (
            <Button type="primary" block onClick={() => handleTakeCharge(record._id)}>
              Prendre en charge
            </Button>
          )}
          <Button block onClick={() => handleDeliver(record._id)}>
            Marquer livré
          </Button>
        </div>
      </Card>
    );
  };

  const renderStats = () => (
    <div>
      <div style={{ marginBottom: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <Button
          type={statsPeriod === 'today' ? 'primary' : 'default'}
          size="small"
          onClick={() => setStatsPeriod('today')}
        >
          Aujourd&apos;hui
        </Button>
        <Button
          type={statsPeriod === '7days' ? 'primary' : 'default'}
          size="small"
          onClick={() => setStatsPeriod('7days')}
        >
          7 jours
        </Button>
        <Button
          type={statsPeriod === '30days' ? 'primary' : 'default'}
          size="small"
          onClick={() => setStatsPeriod('30days')}
        >
          30 jours
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ borderRadius: 12, background: '#fff8e1' }}>
                <Statistic
                  title="À livrer"
                  value={pendingInPeriod.length}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#f9a825', fontSize: isMobile ? 22 : 28 }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={8}>
              <Card size="small" style={{ borderRadius: 12, background: '#e8f5e9' }}>
                <Statistic
                  title="Livrées"
                  value={deliveredOrders.length}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ color: '#43a047', fontSize: isMobile ? 22 : 28 }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card size="small" style={{ borderRadius: 12, background: '#ede7f6' }}>
                <Statistic
                  title="Solde livraisons"
                  value={deliveryRevenue}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#5e35b1', fontSize: isMobile ? 18 : 24 }}
                  formatter={(v) => `${Number(v).toLocaleString('fr-FR')} FCFA`}
                />
              </Card>
            </Col>
          </Row>

          <Card size="small" style={{ marginTop: 12, borderRadius: 12 }} title="En attente (montant)">
            <Text>
              {pendingInPeriod.length} livraison{pendingInPeriod.length > 1 ? 's' : ''} —{' '}
              <Text strong style={{ color: '#f9a825' }}>
                {pendingRevenue.toLocaleString('fr-FR')} FCFA
              </Text>
            </Text>
          </Card>

          <Card
            size="small"
            style={{ marginTop: 12, borderRadius: 12 }}
            title="Solde par zone"
          >
            {deliveryByZone.length === 0 ? (
              <Empty description="Aucune livraison sur la période" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            ) : (
              <Table
                size="small"
                pagination={false}
                rowKey="name"
                dataSource={deliveryByZone}
                columns={[
                  { title: 'Zone', dataIndex: 'name', key: 'name' },
                  { title: 'En attente', dataIndex: 'pending', key: 'pending' },
                  { title: 'Livrées', dataIndex: 'count', key: 'count' },
                  {
                    title: 'Solde',
                    dataIndex: 'amount',
                    key: 'amount',
                    render: (amount: number) => (
                      <Text strong style={{ color: '#5e35b1' }}>
                        {amount.toLocaleString('fr-FR')} FCFA
                      </Text>
                    ),
                  },
                ]}
                summary={() => (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}><strong>Total</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={1}><strong>{pendingInPeriod.length}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={2}><strong>{deliveredOrders.length}</strong></Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>
                      <strong style={{ color: '#5e35b1' }}>
                        {deliveryRevenue.toLocaleString('fr-FR')} FCFA
                      </strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                )}
              />
            )}
          </Card>
        </>
      )}
    </div>
  );

  return (
    <div>
      <Title level={isMobile ? 4 : 3} style={{ marginBottom: 4 }}>
        <CarOutlined /> Espace livreur
      </Title>
      <Text type="secondary" style={{ fontSize: isMobile ? 13 : 14 }}>
        Zone, montant de livraison et statistiques.
      </Text>

      <div style={{ marginTop: 12, marginBottom: 12 }}>
        <Segmented
          block={isMobile}
          value={viewMode}
          onChange={(v) => setViewMode(v as ViewMode)}
          options={[
            { label: 'Livraisons', value: 'livraisons' },
            { label: 'Statistiques', value: 'statistiques' },
          ]}
        />
      </div>

      <Card
        style={{ marginBottom: 16, borderRadius: 12 }}
        styles={{ body: { padding: isMobile ? 12 : 16 } }}
      >
        <Select
          mode="multiple"
          allowClear
          showSearch
          size={isMobile ? 'large' : 'middle'}
          placeholder="Filtrer par zone (optionnel)"
          value={selectedZoneIds}
          onChange={handleZonesChange}
          style={{ width: '100%' }}
          optionFilterProp="label"
          options={zones.map((z) => ({
            value: z._id,
            label: `${z.name} — ${z.price.toLocaleString('fr-FR')} FCFA`,
          }))}
        />
        {viewMode === 'livraisons' && (
          <Input
            allowClear
            placeholder="Rechercher par nom ou téléphone"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: '100%', marginTop: 12 }}
            size={isMobile ? 'large' : 'middle'}
          />
        )}
      </Card>

      {viewMode === 'statistiques' ? (
        renderStats()
      ) : isMobile ? (
        <div>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>
          ) : visiblePendingOrders.length === 0 ? (
            <Empty description="Aucune livraison en attente" />
          ) : (
            <>
              <Text type="secondary" style={{ fontSize: 13 }}>
                {visiblePendingOrders.length} livraison{visiblePendingOrders.length > 1 ? 's' : ''} — solde en attente{' '}
                <Text strong>
                  {visiblePendingOrders.reduce((s, o) => s + getDeliveryFee(o), 0).toLocaleString('fr-FR')} FCFA
                </Text>
              </Text>
              <div style={{ marginTop: 8 }}>{visiblePendingOrders.map(renderMobileCard)}</div>
            </>
          )}
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={visiblePendingOrders}
          rowKey="_id"
          loading={loading}
          scroll={{ x: 'max-content' }}
        />
      )}
    </div>
  );
};

export default DeliveryDriverView;
